<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { captchaGate } from '../../stores/captchaGate.js';
import { reportCaptchaUnavailable } from '../../utilities/captchaReporting.js';
import { _mft } from '../../i18n.js';

const props = defineProps({
    frameBase: {
        type: String,
        required: true,
    },
    fieldName: {
        type: String,
        default: 'cf-turnstile-response',
    },
    action: {
        type: String,
        default: 'register',
    },
});

const emit = defineEmits(['update:solved', 'update:status']);

// How long to wait for the frame's `ready` before assuming it will never arrive.
// Generous, because it covers a cold third party load on a slow connection.
const FRAME_TIMEOUT_MS = 10000;

const token = ref('');
const frame = ref(null);
// Bumping the key remounts the iframe, which is the only way to get a fresh
// challenge from here — turnstile.reset() lives inside the frame's own document.
const frameKey = ref(0);
// loading  — waiting for the bridge to announce itself
// waiting  — widget is up, visitor has not solved it yet
// solved   — we hold a token
// unavailable — the widget cannot be used; the visitor needs a way out
const status = ref('loading');

let frameTimeout = null;

const frameOrigin = computed(() => new URL(props.frameBase).origin);

const frameSrc = computed(() => {
    const query = new URLSearchParams({
        origin: window.location.origin,
        action: props.action,
    });

    return `${frameOrigin.value}/captcha/turnstile?${query.toString()}`;
});

function setStatus(next) {
    status.value = next;
    captchaGate.solved = next === 'solved';
    emit('update:solved', next === 'solved');
    emit('update:status', next);
}

function markUnavailable(reason) {
    token.value = '';
    setStatus('unavailable');
    reportCaptchaUnavailable(props.action, reason);
}

function clearFrameTimeout() {
    if (frameTimeout !== null) {
        clearTimeout(frameTimeout);
        frameTimeout = null;
    }
}

function startFrameTimeout() {
    clearFrameTimeout();

    frameTimeout = setTimeout(() => {
        frameTimeout = null;

        if (status.value === 'loading') {
            markUnavailable('frame-timeout');
        }
    }, FRAME_TIMEOUT_MS);
}

function retry() {
    clearFrameTimeout();
    token.value = '';
    frameKey.value += 1;
    setStatus('loading');
    startFrameTimeout();
}

function handleMessage(event) {
    if (event.origin !== frameOrigin.value || event.source !== frame.value?.contentWindow) {
        return;
    }

    const data = event.data;

    if (!data || data.source !== 'myflow-turnstile') {
        return;
    }

    // Switch on the type explicitly: treating "anything that is not a token" as
    // an empty token would let a late `ready` wipe a token we already hold.
    switch (data.type) {
        case 'ready':
            clearFrameTimeout();

            if (status.value === 'loading') {
                setStatus('waiting');
            }
            break;
        case 'token':
            clearFrameTimeout();
            token.value = data.token || '';
            setStatus(token.value === '' ? 'waiting' : 'solved');
            break;
        case 'expired':
            // Turnstile refreshes expired challenges by itself, so this is a
            // return to waiting rather than a failure.
            token.value = '';
            setStatus('waiting');
            break;
        case 'error':
            markUnavailable('widget-error');
            break;
        case 'unavailable':
            markUnavailable(data.reason || 'script-blocked');
            break;
    }
}

onMounted(() => {
    setStatus('loading');
    window.addEventListener('message', handleMessage);
    startFrameTimeout();
});

onBeforeUnmount(() => {
    window.removeEventListener('message', handleMessage);
    clearFrameTimeout();
    captchaGate.solved = true;
});

defineExpose({ retry });
</script>

<template>
    <div class="flex flex-col items-center py-2">
        <input type="hidden" :name="fieldName" :value="token" />
        <iframe
            v-show="status !== 'unavailable'"
            :key="frameKey"
            ref="frame"
            :src="frameSrc"
            title="Captcha"
            class="border-0"
            width="300"
            height="70"
            scrolling="no"
        ></iframe>
        <div v-if="status === 'unavailable'" class="text-center">
            <p class="text-sm">{{ _mft('auth:captcha.unavailable') }}</p>
            <button-secondary size="sm" class="mt-2" @click="retry">
                {{ _mft('auth:captcha.retry') }}
            </button-secondary>
        </div>
    </div>
</template>
